import { useQuery } from "@apollo/client";
import { MultiValueTextInput } from "components/MultiValueTextInput";
import { gql } from "generated-graphql";
import { ComponentProps } from "react";

export const FACILITY_WASTE_CODES = gql(`
  query FilterFacilityWasteCodes($facilityId: ID!) {
    facility(id: $facilityId) {
      id
      wasteCodes {
        federalWasteCodes
        stateWasteCodes
      }
    }
  }
`);

export const TENANT_WASTE_CODES = gql(`
  query FilterTenantWasteCodes($tenantId: ID!, $facilityIds: [ID!]) {
    tenantWasteCodes(tenantId: $tenantId, facilityIds: $facilityIds) {
      federalWasteCodes
      stateWasteCodes
    }
  }
`);

type WasteCodeFilterProps = Omit<
  ComponentProps<typeof MultiValueTextInput>,
  "parseInput" | "autoSelect" | "options" | "loading" | "onInputChange" | "maxSuggestions"
> & {
  kind: "federal" | "state";
  tenantId?: string;
  facilityId?: string;
  facilityIds?: string[];
};

/** Split only unquoted separators; spaces within a value remain literal. */
function parseTextList(input: string): string[] {
  const values: string[] = [];
  let value = "";
  let quoted = false;

  for (const character of input) {
    if (character === '"') {
      quoted = !quoted;
    } else if (!quoted && (character === "," || character === "|")) {
      values.push(value);
      value = "";
    } else {
      value += character;
    }
  }

  values.push(value);

  return [
    ...new Set(
      values.flatMap((entry) => {
        const trimmed = entry.trim();

        return trimmed ? [trimmed] : [];
      })
    ),
  ];
}

export function WasteCodeFilter({
  kind,
  tenantId,
  facilityId,
  facilityIds,
  value,
  ...props
}: WasteCodeFilterProps) {
  const facility = useQuery(FACILITY_WASTE_CODES, {
    variables: { facilityId: facilityId ?? "" },
    skip: !facilityId,
  });

  const tenant = useQuery(TENANT_WASTE_CODES, {
    variables: { tenantId: tenantId ?? "", facilityIds },
    skip: !!facilityId || !tenantId,
  });

  const codes = facilityId ? facility.data?.facility?.wasteCodes : tenant.data?.tenantWasteCodes;
  const options = kind === "federal" ? codes?.federalWasteCodes : codes?.stateWasteCodes;

  return (
    <MultiValueTextInput
      {...props}
      value={value}
      options={[...new Set([...(options ?? []), ...(value ?? [])])].sort()}
      maxSuggestions={50}
      loading={facilityId ? facility.loading : tenant.loading}
      parseInput={parseTextList}
      autoSelect
    />
  );
}
